import React, { useReducer, useState } from "react";
import styled from "@emotion/styled";
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { DialogModal } from "../DialogModal/DialogModal";
import { StepIndicator } from "./StepIndicator";
import { Step1NameEmail } from "./steps/Step1NameEmail";
import { Step2BillingAddress } from "./steps/Step2BillingAddress";
import { Step3Payment } from "./steps/Step3Payment";
import { reducer, initialState } from "./reducer";
import {
  ImpactShopCheckoutRegistrationProps,
  FormData,
  BillingAddress,
  Step,
} from "./types";

const ModalContent = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    padding: 32px;
  }
`;

const StyledModal = styled(DialogModal)`
  max-width: 800px;
  width: 90%;

  @media ${({ theme }) => theme.bp.tabletDown} {
    width: 100%;
    max-width: 100%;
  }
`;

type ImpactShopCheckoutRegistrationInternalProps =
  ImpactShopCheckoutRegistrationProps & {
    stripe: Stripe | null;
  };

const ImpactShopCheckoutRegistrationBase = ({
  isOpen,
  onClose,
  onSignIn,
  onCheckoutComplete,
  onStepComplete,
  basket,
  currency,
  location,
  logger,
}: Omit<ImpactShopCheckoutRegistrationInternalProps, "stripe">) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();

  const handleClose = () => {
    dispatch({ type: "CLEAR_ERRORS" });
    dispatch({ type: "SET_EMAIL_ERROR", payload: null });
    setPaymentError(null);
    onClose();
  };

  const handleStep1Continue = (
    data: Pick<
      FormData,
      "firstName" | "lastName" | "email" | "accountType" | "termsAccepted"
    >
  ) => {
    dispatch({ type: "UPDATE_FORM_DATA", payload: data });
    dispatch({ type: "SET_STEP", payload: 2 });
    onStepComplete?.(1);
  };

  const handleStep2Continue = (data: BillingAddress) => {
    dispatch({ type: "UPDATE_BILLING_ADDRESS", payload: data });
    dispatch({ type: "SET_STEP", payload: 3 });
    onStepComplete?.(2);
  };

  const handleBack = (step: Step) => {
    dispatch({ type: "SET_STEP", payload: step });
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setPaymentError("Payment system is not ready. Please try again.");
      return;
    }

    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    setPaymentError(null);

    try {
      const cardElement = elements.getElement("cardNumber");
      if (!cardElement) {
        setPaymentError("Card details are not available. Please try again.");
        return;
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${state.formData.firstName} ${state.formData.lastName}`,
          email: state.formData.email,
          address: {
            line1: state.formData.billingAddress.address1,
            line2: state.formData.billingAddress.address2 || undefined,
            city: state.formData.billingAddress.city,
            postal_code: state.formData.billingAddress.postcode,
            country: state.formData.billingAddress.country || undefined,
          },
        },
      });

      if (error || !paymentMethod) {
        setPaymentError(
          error?.message ||
            "Failed to process card details. Please check your card information and try again."
        );
        return;
      }

      await onCheckoutComplete({
        formData: state.formData,
        paymentMethodId: paymentMethod.id,
      });
    } catch (err) {
      logger.error("Payment submission failed", { error: err });
      setPaymentError("An unexpected error occurred. Please try again.");
      // Only reset on error so the user can retry. On success the modal
      // closes and the component unmounts, so resetting in finally would
      // briefly flash the non-submitting state and trigger a basket re-render.
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const renderStep = () => {
    switch (state.step) {
      case 1:
        return (
          <Step1NameEmail
            formData={state.formData}
            emailError={state.emailError}
            isSubmitting={state.isSubmitting}
            onSignIn={onSignIn}
            onContinue={handleStep1Continue}
          />
        );
      case 2:
        return (
          <Step2BillingAddress
            formData={state.formData}
            isSubmitting={state.isSubmitting}
            defaultCountry={location || ""}
            onContinue={handleStep2Continue}
            onBack={() => handleBack(1)}
          />
        );
      case 3:
        return (
          <Step3Payment
            formData={state.formData}
            isSubmitting={state.isSubmitting}
            basket={basket}
            currency={currency}
            paymentError={paymentError}
            onSubmit={handleSubmit}
            onBack={() => handleBack(2)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={handleClose}
      closable={!state.isSubmitting}
      onBack={
        state.step > 1 ? () => handleBack((state.step - 1) as Step) : undefined
      }
      scrollable
      dataTestId="impact-shop-checkout-registration-modal"
    >
      <ModalContent>
        <StepIndicator currentStep={state.step} />
        {renderStep()}
      </ModalContent>
    </StyledModal>
  );
};

export type ImpactShopCheckoutRegistrationWithStripeProps =
  ImpactShopCheckoutRegistrationProps & {
    stripe: Stripe | null;
  };

export const ImpactShopCheckoutRegistration = ({
  stripe,
  ...props
}: ImpactShopCheckoutRegistrationWithStripeProps) => {
  return (
    <Elements stripe={stripe}>
      <ImpactShopCheckoutRegistrationBase {...props} />
    </Elements>
  );
};
